import { authorizedRequest } from "../wire";
import { freshCredential, type Session } from "../sign-in";

// The release Worker's deck endpoints (mtg/platform/deck_endpoints.spell), relative to the page so
// a release's editor under /r/<n>/ reaches that release's.

export type Zone = "main" | "sideboard" | "commander";
export type FormatName = "Commander" | "Standard";
export type DeckView = "list" | "art";
export type Legality = "Legal" | "Banned" | "Restricted" | "NotLegal";

export type Card = {
  oracle_id: string;
  name: string;
  mana_cost: string;
  mana_value: number;
  type_line: string;
  oracle_text: string;
  color_identity: string[];
  // In the deck's format, or the one a search named.
  legality: Legality;
  can_be_commander: boolean;
  // Whether it may share the command zone with a second commander.
  can_pair: boolean;
  // Whether this release has its card file.
  playable: boolean;
};

export type DeckLine = { count: number; zone: Zone; card: Card };

// A line whose oracle id no card has: kept, reported, and sent back unchanged on every save.
export type UnknownLine = { oracle_id: string; count: number; zone: Zone };

export type Check = {
  label: string;
  // [] when the deck keeps the rule.
  problems: string[];
  cards: string[];
  // Every room refuses a deck that breaks it, Casual or Strict.
  always_enforced: boolean;
};

export type Deck = {
  deck_id: string;
  name: string;
  format: FormatName;
  starter: boolean;
  updated_at: number;
  lines: DeckLine[];
  unknown_lines: UnknownLine[];
  checks: Check[];
};

export type DeckSummary = { deck_id: string; name: string; format: FormatName; starter: boolean };

export type CardPage = { cards: Card[]; page: number; has_more: boolean };

export type CardQuery = {
  text: string;
  types: string[];
  // Color letters a card's identity lies inside; null for any.
  colors: string | null;
  playable: boolean;
  commanders: boolean;
  format: FormatName;
  page: number;
};

export type SavedLine = { oracle_id: string; count: number; zone: Zone };

export type ExportedDeck = { text: string; fileName: string };

export class EndpointError extends Error {
  constructor(readonly status: number, message: string, readonly unknownCards: string[] = []) {
    super(message);
  }
}

const deckPath = (deckId: string, rest = ""): string => `decks/${encodeURIComponent(deckId)}${rest}`;

export class DeckEndpoints {
  constructor(private readonly session: Session) {}

  private async sent(method: string, path: string, body?: object): Promise<Response> {
    const init: RequestInit = body === undefined ? { method } : {
      method, body: JSON.stringify(body), headers: { "Content-Type": "application/json" },
    };
    const [url, authorized] = authorizedRequest(path, await freshCredential(this.session), init);
    const response = await fetch(url, authorized);
    if (response.ok) return response;
    const text = await response.text();
    let error: { error?: string; unknown_cards?: string[] } = {};
    try { error = JSON.parse(text); } catch { /* a plain-text answer, such as a 401 from sign-in */ }
    throw new EndpointError(response.status, error.error ?? (text || `${method} ${path} answered ${response.status}`), error.unknown_cards ?? []);
  }

  private async json<T>(method: string, path: string, body?: object): Promise<T> {
    return (await this.sent(method, path, body)).json() as Promise<T>;
  }

  async listDecks(): Promise<DeckSummary[]> {
    return (await this.json<{ decks: DeckSummary[] }>("GET", "decks")).decks;
  }

  readDeck(deckId: string): Promise<Deck> {
    return this.json("GET", deckPath(deckId));
  }

  createDeck(name: string, format: FormatName): Promise<Deck> {
    return this.json("POST", "decks", { name, format: format.toLowerCase() });
  }

  // Refused with the names no card has, in `unknownCards`.
  importDeck(name: string, text: string): Promise<Deck> {
    return this.json("POST", "decks/import", { name, text });
  }

  // A public Archidekt deck, named as it is there; refused as importDeck is, or with why Archidekt gave none.
  importArchidektDeck(url: string): Promise<Deck> {
    return this.json("POST", "decks/import", { url });
  }

  renameDeck(deckId: string, name: string): Promise<Deck> {
    return this.json("PUT", deckPath(deckId, "/name"), { name });
  }

  changeDeckFormat(deckId: string, format: FormatName): Promise<Deck> {
    return this.json("PUT", deckPath(deckId, "/format"), { format: format.toLowerCase() });
  }

  replaceDeckLines(deckId: string, lines: SavedLine[]): Promise<Deck> {
    return this.json("PUT", deckPath(deckId, "/lines"), { lines });
  }

  async deleteDeck(deckId: string): Promise<void> {
    await this.sent("DELETE", deckPath(deckId));
  }

  copyDeck(deckId: string): Promise<Deck> {
    return this.json("POST", deckPath(deckId, "/copy"), {});
  }

  // The file name is the one the Worker's Content-Disposition gives.
  async exportDeck(deckId: string): Promise<ExportedDeck> {
    const response = await this.sent("GET", deckPath(deckId, "/export"));
    const fileName = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? "deck.deck";
    return { text: await response.text(), fileName };
  }

  searchCards(query: CardQuery): Promise<CardPage> {
    const params = new URLSearchParams({ q: query.text, format: query.format.toLowerCase(), page: String(query.page) });
    if (query.types.length > 0) params.set("types", query.types.join(","));
    if (query.colors !== null) params.set("colors", query.colors);
    if (query.playable) params.set("playable", "1");
    if (query.commanders) params.set("commanders", "1");
    return this.json("GET", `cards?${params}`);
  }

  async readDeckView(): Promise<DeckView> {
    return (await this.json<{ deck_view: DeckView }>("GET", "preferences/deck-view")).deck_view;
  }

  async saveDeckView(view: DeckView): Promise<void> {
    await this.sent("PUT", "preferences/deck-view", { deck_view: view });
  }
}
